import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Loader2, Search, X, Check, Image as ImageIcon } from 'lucide-react';
import axiosInstance from '@/utils/axiosInstance';
import { useToast } from '@/hooks/use-toast';

const COUNTRIES = [
  { code: 'BE', flag: '🇧🇪' },
  { code: 'NL', flag: '🇳🇱' },
  { code: 'FR', flag: '🇫🇷' },
  { code: 'DE', flag: '🇩🇪' },
  { code: 'UK', flag: '🇬🇧' },
];

export default function AlertFormDialog({ open, onClose, alertId, onSaved }) {
  const { t } = useTranslation('alerts');
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [thresholdPct, setThresholdPct] = useState('15');
  const [countries, setCountries] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [loadingInitial, setLoadingInitial] = useState(false);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchDebounceRef = useRef(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (!open) return;
    setName('');
    setThresholdPct('15');
    setCountries([]);
    setIsActive(true);
    setSelectedProducts([]);
    setSearch('');
    setSearchResults([]);
  }, [open]);

  // Load alert when editing
  useEffect(() => {
    if (!open || !alertId) return;
    setLoadingInitial(true);
    axiosInstance
      .get(`/api/alerts/${alertId}/`)
      .then(({ data }) => {
        setName(data.name);
        setThresholdPct(String(data.threshold_pct));
        setCountries(data.countries || []);
        setIsActive(data.is_active);
        setSelectedProducts(
          (data.products || []).map(p => ({
            shopify_id: p.shopify_id,
            ean: p.ean,
            title: p.title || (p.shopify_id ? `#${p.shopify_id}` : p.ean),
            image: p.image,
            sku: p.sku,
          }))
        );
      })
      .catch(() => {
        toast({
          title: t('errors.fetchTitle'),
          description: t('errors.fetchDescription'),
          variant: 'destructive',
        });
      })
      .finally(() => setLoadingInitial(false));
  }, [open, alertId]);

  // Debounced product search
  useEffect(() => {
    if (!open) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    if (!search.trim()) {
      setSearchResults([]);
      return;
    }

    searchDebounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axiosInstance.get('/api/products/', {
          params: { search: search.trim(), page_size: 30 },
        });
        setSearchResults(data.results || data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search, open]);

  const toggleCountry = (code) => {
    setCountries(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const isProductSelected = useCallback(
    (p) => selectedProducts.some(sp =>
      (sp.shopify_id && sp.shopify_id === p.shopify_id) ||
      (sp.ean && sp.ean === p.ean)
    ),
    [selectedProducts]
  );

  const toggleProduct = (p) => {
    if (isProductSelected(p)) {
      setSelectedProducts(prev =>
        prev.filter(sp =>
          !((sp.shopify_id && sp.shopify_id === p.shopify_id) ||
            (sp.ean && sp.ean === p.ean))
        )
      );
    } else {
      setSelectedProducts(prev => [...prev, {
        shopify_id: p.shopify_id || null,
        ean: p.shopify_id ? null : (p.v_ean || p.ean || null),
        title: p.p_title || p.title || p.name,
        image: p.v_image_link || p.image,
        sku: p.v_sku || p.sku,
      }]);
    }
  };

  const removeProduct = (p) => {
    setSelectedProducts(prev =>
      prev.filter(sp =>
        !((sp.shopify_id && sp.shopify_id === p.shopify_id) ||
          (sp.ean && sp.ean === p.ean))
      )
    );
  };

  const validate = () => {
    if (!name.trim()) return t('form.errors.nameRequired');
    const thr = parseFloat(thresholdPct);
    if (Number.isNaN(thr) || thr <= 0 || thr > 100) {
      return t('form.errors.thresholdInvalid');
    }
    if (selectedProducts.length === 0) return t('form.errors.productsRequired');
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      toast({ title: t('form.errors.invalidTitle'), description: err, variant: 'destructive' });
      return;
    }

    setSaving(true);
    const payload = {
      name: name.trim(),
      threshold_pct: parseFloat(thresholdPct),
      countries,
      is_active: isActive,
      products: selectedProducts.map(p => ({
        shopify_id: p.shopify_id,
        ean: p.ean,
      })),
    };

    try {
      if (alertId) {
        await axiosInstance.patch(`/api/alerts/${alertId}/update/`, payload);
        toast({ title: t('form.updated') });
      } else {
        await axiosInstance.post('/api/alerts/create/', payload);
        toast({ title: t('form.created') });
      }
      onSaved?.();
      onClose();
    } catch (e) {
      const detail = e.response?.data
        ? Object.values(e.response.data).flat().join(' ')
        : '';
      toast({
        title: t('form.errors.saveTitle'),
        description: detail || t('form.errors.saveDescription'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{alertId ? t('form.editTitle') : t('form.createTitle')}</DialogTitle>
          <DialogDescription>{t('form.description')}</DialogDescription>
        </DialogHeader>

        {loadingInitial ? (
          <div className="flex justify-center py-12 px-6">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6 px-6 pb-2">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="alert-name">{t('form.nameLabel')}</Label>
              <Input
                id="alert-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('form.namePlaceholder')}
                maxLength={255}
              />
            </div>

            {/* Threshold */}
            <div className="space-y-2">
              <Label htmlFor="alert-threshold">{t('form.thresholdLabel')}</Label>
              <div className="relative">
                <Input
                  id="alert-threshold"
                  type="number"
                  min={0.01}
                  max={100}
                  step={0.5}
                  value={thresholdPct}
                  onChange={(e) => setThresholdPct(e.target.value)}
                  className="pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  %
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('form.thresholdHelp')}
              </p>
            </div>

            {/* Countries */}
            <div className="space-y-2">
              <Label>{t('form.countriesLabel')}</Label>
              <div className="flex flex-wrap gap-2">
                {COUNTRIES.map(({ code, flag }) => {
                  const selected = countries.includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => toggleCountry(code)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm transition-colors ${
                        selected
                          ? 'bg-amber-100 border-amber-300 text-amber-900'
                          : 'bg-background border-input hover:bg-muted'
                      }`}
                    >
                      <span>{flag}</span>
                      <span>{code}</span>
                      {selected && <Check className="h-3 w-3" />}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                {countries.length === 0
                  ? t('form.countriesAllHelp')
                  : t('form.countriesSelectedHelp', { count: countries.length })}
              </p>
            </div>

            {/* Products */}
            <div className="space-y-2">
              <Label>{t('form.productsLabel')}</Label>

              {/* Selected chips */}
              {selectedProducts.length > 0 && (
                <div className="rounded-md border bg-muted/30 p-3 space-y-2 max-h-40 overflow-y-auto">
                  <div className="text-xs font-medium text-muted-foreground">
                    {t('productsCount', { count: selectedProducts.length })}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedProducts.map(p => (
                      <Badge
                        key={p.shopify_id || p.ean}
                        variant="secondary"
                        className="gap-1 pr-1 max-w-xs"
                      >
                        <span className="truncate">{p.title}</span>
                        <button
                          type="button"
                          onClick={() => removeProduct(p)}
                          className="ml-1 hover:bg-background rounded-sm p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('form.productsSearchPlaceholder')}
                  className="pl-9"
                />
              </div>

              {/* Results */}
              {search.trim() && (
                <div className="rounded-md border max-h-64 overflow-y-auto">
                  {searching ? (
                    <div className="flex justify-center py-6">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : searchResults.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">
                      {t('form.productsNoResults')}
                    </p>
                  ) : (
                    <ul className="divide-y">
                      {searchResults.map(p => {
                        const selected = isProductSelected(p);
                        return (
                          <li
                            key={p.shopify_id || p.v_ean}
                            onClick={() => toggleProduct(p)}
                            className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                              selected ? 'bg-amber-50' : 'hover:bg-muted/50'
                            }`}
                          >
                            <div className="h-10 w-10 shrink-0 rounded bg-muted flex items-center justify-center overflow-hidden">
                              {p.v_image_link ? (
                                <img
                                  src={p.v_image_link}
                                  alt=""
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <ImageIcon className="h-4 w-4 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">
                                {p.p_title}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">
                                {p.v_sku || p.v_ean || ''}
                              </div>
                            </div>
                            {selected && (
                              <Check className="h-4 w-4 text-amber-700 shrink-0" />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Active */}
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label className="text-base">{t('form.activeLabel')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('form.activeHelp')}
                </p>
              </div>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('form.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving || loadingInitial}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {alertId ? t('form.save') : t('form.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
